import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart, cartMessage, cartTotal, canCart } from "@/lib/cart";
import { waLink } from "@/lib/offerUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingCart, Minus, Plus, Trash2, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PICKUPS = ["As soon as possible", "In 15 minutes", "In 30 minutes", "In 1 hour"];
const lsGet = (k) => { try { return localStorage.getItem(k) || ""; } catch { return ""; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };

// Floating cart + dialog. Food shops: customer places a pre-order and gets a token number (tracked on a
// status page). Other shops: the cart becomes one WhatsApp message (prices are free text, no totals).
export default function CartWidget({ slug, business, pageUrl, trackPath }) {
  const { items, count, setQty, clear } = useCart(slug);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [name, setName] = useState(() => lsGet("dw_cust_name"));
  const [phone, setPhone] = useState(() => lsGet("dw_cust_phone"));
  const [pickup, setPickup] = useState(PICKUPS[0]);
  const [placing, setPlacing] = useState(false);
  const food = business?.mode === "food";
  if (!canCart(business) || count === 0) return null;
  const total = cartTotal(items);

  const sendWhatsApp = () => {
    api.post(`/public/${slug}/event`, { type: "whatsapp" }).catch(() => {});
    window.open(waLink(business.whatsapp, cartMessage(business.name, items, pageUrl, note.trim())), "_blank", "noopener");
    clear(); setOpen(false); setNote("");
  };

  const placeOrder = async () => {
    if (!name.trim()) return toast.error("Enter your name");
    if (phone.replace(/\D/g, "").length < 7) return toast.error("Enter a valid phone number");
    setPlacing(true);
    try {
      const { data } = await api.post(`/public/${slug}/orders`, {
        customer_name: name.trim(), phone: phone.trim(), pickup_time: pickup, note: note.trim(),
        items: items.map((i) => ({ offer_id: i.id, qty: i.qty })),
      });
      lsSet("dw_cust_name", name.trim()); lsSet("dw_cust_phone", phone.trim());
      clear(); setOpen(false); setNote("");
      navigate(trackPath(data.order_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't place the order");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="fixed bottom-4 left-4 z-40 bg-primary hover:bg-secondary text-white rounded-full shadow-lg px-5 py-3 flex items-center gap-2 text-sm font-semibold" data-testid="cart-btn">
        <ShoppingCart className="w-5 h-5" /> {food ? "Order" : "Cart"} ({count}){food && total != null ? ` · Rs ${total.toLocaleString()}` : ""}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto" data-testid="cart-dialog">
          <DialogHeader><DialogTitle className="font-display">{food ? "Place your pre-order" : "Your order"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {items.map((i) => (
              <div key={i.id} className="flex items-center gap-3 border border-white/10 rounded-lg p-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{i.title}</p>
                  {i.price && <p className="text-xs text-muted-foreground">{i.price}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <button aria-label="less" className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center" onClick={() => setQty(i.id, i.qty - 1)}>{i.qty === 1 ? <Trash2 className="w-3 h-3" /> : <Minus className="w-3 h-3" />}</button>
                  <span className="w-5 text-center text-sm" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                  <button aria-label="more" className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center" onClick={() => setQty(i.id, Math.min(i.qty + 1, 20))}><Plus className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
            {food && total != null && <p className="text-sm text-right font-semibold" data-testid="cart-total">Total: Rs {total.toLocaleString()}</p>}

            {food && (
              <>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={60} className="bg-background border-white/10" data-testid="order-name" />
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number (03xx xxxxxxx)" inputMode="tel" maxLength={20} className="bg-background border-white/10" data-testid="order-phone" />
                <select value={pickup} onChange={(e) => setPickup(e.target.value)} className="w-full rounded-md bg-background border border-white/10 p-2 text-sm" data-testid="order-pickup">
                  {PICKUPS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </>
            )}
            <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder={food ? "Note (no onions, extra spicy...)" : "Note for the shop (name, address, size...)"} className="w-full rounded-lg bg-background border border-white/10 p-3 text-sm outline-none focus:border-primary" rows={2} />

            {food ? (
              <>
                <Button onClick={placeOrder} disabled={placing} className="w-full rounded-full bg-primary hover:bg-secondary" data-testid="place-order-btn">
                  {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Place pre-order"}
                </Button>
                <p className="text-xs text-muted-foreground text-center">You'll get a token number. Pay when you collect your food.</p>
              </>
            ) : (
              <>
                <Button onClick={sendWhatsApp} className="w-full rounded-full bg-green-600 hover:bg-green-700" data-testid="cart-send-btn"><MessageCircle className="w-4 h-4 mr-1" /> Send order on WhatsApp</Button>
                <p className="text-xs text-muted-foreground text-center">The shop will confirm the final price with you on WhatsApp.</p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
